// Hand-written RFC 4180 parser: quoted fields, escaped quotes, embedded newlines, CRLF/LF/CR, BOM.

export interface CsvOptions {
  /** Force a delimiter; otherwise auto-detected from the first line among , ; \t |. */
  delimiter?: string;
}

function detectDelimiter(text: string): string {
  let inQuotes = false;
  const counts: Record<string, number> = { ",": 0, ";": 0, "\t": 0, "|": 0 };
  for (const ch of text) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes) {
      if (ch === "\n" || ch === "\r") break;
      if (ch in counts) counts[ch] = (counts[ch] ?? 0) + 1;
    }
  }
  let best = ",";
  for (const d of Object.keys(counts)) if ((counts[d] ?? 0) > (counts[best] ?? 0)) best = d;
  return best;
}

export function parseCsv(input: string, opts: CsvOptions = {}): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const delim = opts.delimiter ?? detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let quoteRow = 0;
  let i = 0;
  const endRow = () => {
    row.push(field);
    field = "";
    rows.push(row);
    row = [];
  };
  while (i < text.length) {
    const ch = text[i] as string;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += ch; i++; continue;
    }
    if (ch === '"' && field === "") { inQuotes = true; quoteRow = rows.length + 1; i++; continue; }
    if (ch === delim) { row.push(field); field = ""; i++; continue; }
    if (ch === "\r") { endRow(); i += text[i + 1] === "\n" ? 2 : 1; continue; }
    if (ch === "\n") { endRow(); i++; continue; }
    field += ch; i++;
  }
  // Otherwise every row after a stray opening quote silently disappears into one cell.
  if (inQuotes) throw new Error(`Unterminated quote starting in row ${quoteRow}`);
  if (field !== "" || row.length > 0) endRow();
  return rows;
}

export interface SheetTable {
  headers: string[];
  rows: string[][];
  /** 1-based sheet row number of each data row (header row is row `headerRow + 1`). */
  rowNumbers: number[];
  /** Sheet row numbers with non-blank cells past the header width: usually an unquoted comma, so columns may have shifted. */
  overflowRows?: number[];
}

/** Accepts raw `string[][]` (CSV or XLSX-derived). Skips fully blank rows, pads/truncates to header width; rows that lost data are listed in `overflowRows`. */
export function toTable(raw: string[][], opts: { headerRow?: number } = {}): SheetTable {
  const hr = opts.headerRow ?? 0;
  const headerCells = raw[hr] ?? [];
  const headers = headerCells.map((h) => h.trim());
  const width = headers.length;
  const rows: string[][] = [];
  const rowNumbers: number[] = [];
  const overflowRows: number[] = [];
  for (let r = hr + 1; r < raw.length; r++) {
    const src = raw[r] ?? [];
    if (src.every((c) => c.trim() === "")) continue;
    const cells: string[] = [];
    for (let c = 0; c < width; c++) cells.push((src[c] ?? "").trim());
    rows.push(cells);
    rowNumbers.push(r + 1);
    if (src.slice(width).some((c) => c.trim() !== "")) overflowRows.push(r + 1);
  }
  return { headers, rows, rowNumbers, ...(overflowRows.length ? { overflowRows } : {}) };
}
